<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { emailValidator, phoneValidator } from '@validators'
import {
  CONTACT_TYPES,
  CONTACT_TYPE_LABELS,
  blankRow,
  cleanContactEntries,
  parseContactEntries,
  toEditableRows,
} from '@/plugins/contactEntries'

/**
 * RM-189 — repeatable name+entry editor for a restaurant's referral-contact
 * append fields. Replaces the legacy comma-separated textarea.
 *
 * Each entry is one row: the email/phone VALUE (primary) + an optional NAME
 * (secondary internal label, not shown in outgoing comms). v-model is the
 * normalized array [{ name, email }] / [{ name, phone }]; empty rows are dropped
 * on emit. A restaurant may still arrive with a legacy comma-String — it is
 * normalized to rows on load (see plugins/contactEntries.js).
 *
 * RM-275 — email rows also carry a delivery role (To / Cc / Bcc). It is a
 * secondary control: nearly every row stays To, so it renders as a compact
 * segmented toggle rather than a third full-height input competing with the
 * address field. Phone rows never show it — SMS has no CC/BCC concept.
 */

const props = defineProps({
  modelValue: { type: [Array, String], default: () => [] },
  valueKey: { type: String, default: 'email' }, // 'email' | 'phone'
  valueLabel: { type: String, default: 'Email' },
  valueType: { type: String, default: 'email' }, // native input type attr
  nameLabel: { type: String, default: 'Name (optional)' },
  addLabel: { type: String, default: 'Add another' },
  hint: { type: String, default: '' },
  requireAtLeastOne: { type: Boolean, default: false },
  // RM-205: an optional boolean per row (e.g. automatedTexts) rendered as a checkbox
  toggleKey: { type: String, default: '' },
  toggleLabel: { type: String, default: '' },
  // RM-205: a caption under each value, keyed by the row's value (e.g. texting status)
  captions: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:modelValue'])

// Stable per-row key so add/remove don't reuse the wrong DOM node. Stripped on emit.
let keySeq = 0
const keyed = list => list.map(r => ({ ...r, _k: keySeq++ }))

const rows = ref(keyed(toEditableRows(props.modelValue, props.valueKey)))

// Tracks the JSON of our last emitted value so the watcher can tell an external
// change (different restaurant loaded) from an echo of our own emission.
let lastEmitted = JSON.stringify(parseContactEntries(rows.value, props.valueKey))

function emitUpdate() {
  const cleaned = cleanContactEntries(rows.value, props.valueKey)
  lastEmitted = JSON.stringify(cleaned)
  emit('update:modelValue', cleaned)
}

watch(() => props.modelValue, val => {
  const incoming = JSON.stringify(parseContactEntries(val, props.valueKey))

  // Ignore echoes of our own emission so in-progress typing isn't clobbered.
  if (incoming !== lastEmitted) {
    rows.value = keyed(toEditableRows(val, props.valueKey))
    lastEmitted = incoming
  }
})

function updateField(i, key, value) {
  rows.value[i][key] = value
  emitUpdate()
}

function addRow() {
  rows.value.push({ ...blankRow(props.valueKey), _k: keySeq++ })
}

function removeRow(i) {
  rows.value.splice(i, 1)
  if (!rows.value.length)
    rows.value.push({ ...blankRow(props.valueKey), _k: keySeq++ })
  emitUpdate()
}

// Roles apply to email lists only — a text message has no CC or BCC.
const supportsRoles = computed(() => props.valueKey === 'email')
const digits = v => String(v || '').replace(/\D/g, '').slice(-10)
function captionFor(row) {
  const v = row && row[props.valueKey]
  if (!v) return ''

  return props.captions[v] || props.captions[digits(v)] || ''
}

const roleItems = CONTACT_TYPES.map(type => ({ title: CONTACT_TYPE_LABELS[type], value: type }))

// The "at least one To" rule lives on the first row's value field, but changing
// a role does not change that field's value — so Vuetify would not re-run it and
// the user would see no error until they tried to save. Re-validate explicitly.
const firstValueField = ref(null)

async function setRole(i, value) {
  // VBtnToggle is mandatory, but guard against an unset emit clearing the role.
  if (!value) return
  updateField(i, 'type', value)
  await nextTick()
  firstValueField.value?.validate?.()
}

// "At least one" is a property of the whole list, so any single filled row
// satisfies it. Attached only to the first row's value field for one clean message.
const atLeastOneRule = () => !props.requireAtLeastOne
  || parseContactEntries(rows.value, props.valueKey).length > 0
  || `Add at least one ${props.valueKey === 'phone' ? 'phone number' : 'email'}`

// RM-275 — an all-Cc/Bcc list would send with an empty To header, which looks
// broken to the restaurant. Like the rule above this is a property of the whole
// list, so it is attached once, to the first row.
const atLeastOneToRule = () => {
  if (!supportsRoles.value) return true

  const entries = parseContactEntries(rows.value, props.valueKey)
  if (!entries.length) return true

  return entries.some(entry => entry.type === 'to')
    || 'At least one recipient must be To'
}

// Format-check a value only when present (blank rows are dropped on save).
function formatRule(v) {
  if (!v)
    return true

  return props.valueKey === 'phone' ? phoneValidator(v) : emailValidator(v)
}

function valueRules(i) {
  return i === 0 ? [atLeastOneRule, atLeastOneToRule, formatRule] : [formatRule]
}
</script>

<template>
  <div class="contact-appends-editor">
    <div
      v-for="(row, i) in rows"
      :key="row._k"
      class="contact-entry mb-8"
    >
      <!--
        One layout at every width. Breakpoint classes key off the VIEWPORT, so
        inside a narrow dialog they still force a wide row and crush fields.
        Role + optional name share the top line; the address gets a full line
        of its own below, since it is the longest value and the one that must
        never truncate.
      -->
      <div class="contact-entry__meta d-flex ga-2 align-start">
        <VSelect
          v-if="supportsRoles"
          :model-value="row.type || 'to'"
          :items="roleItems"
          :aria-label="`Delivery role for ${row[valueKey] || 'this recipient'}`"
          hide-details
          class="contact-entry__role"
          @update:model-value="setRole(i, $event)"
        />
        <VTextField
          :model-value="row.name"
          :label="nameLabel"
          autocomplete="off"
          hide-details="auto"
          class="contact-entry__name flex-grow-1"
          @update:model-value="updateField(i, 'name', $event)"
        />
        <VBtn
          icon
          variant="text"
          size="small"
          color="medium-emphasis"
          class="contact-entry__remove"
          :aria-label="`Remove ${row[valueKey] || 'entry'}`"
          @click="removeRow(i)"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </div>

      <VTextField
        :ref="el => { if (i === 0) firstValueField = el }"
        :model-value="row[valueKey]"
        :label="valueLabel"
        :type="valueType"
        :rules="valueRules(i)"
        autocomplete="off"
        hide-details="auto"
        class="contact-entry__value"
        @update:model-value="updateField(i, valueKey, $event)"
      />
      <div
        v-if="toggleKey || captionFor(row)"
        class="contact-entry__extra d-flex align-center ga-3 mt-1"
      >
        <VCheckbox
          v-if="toggleKey"
          :model-value="!!row[toggleKey]"
          :label="toggleLabel"
          density="compact"
          hide-details
          class="contact-entry__toggle"
          @update:model-value="updateField(i, toggleKey, !!$event)"
        />
        <span
          v-if="captionFor(row)"
          class="text-caption text-medium-emphasis"
        >{{ captionFor(row) }}</span>
      </div>
    </div>

    <VBtn
      variant="text"
      size="small"
      color="primary"
      class="px-1"
      @click="addRow"
    >
      <VIcon start>
        mdi-plus
      </VIcon>
      {{ addLabel }}
    </VBtn>

    <div
      v-if="hint"
      class="text-caption text-medium-emphasis mt-1"
    >
      {{ hint }}
    </div>
  </div>
</template>

<style scoped>
/* iOS Safari zooms any input with font-size < 16px on focus (per CLAUDE.md). */
.contact-appends-editor :deep(input) {
  font-size: 16px;
}

.contact-appends-editor :deep(.v-select .v-field__input) {
  font-size: 16px;
}

/*
 * The role is secondary to the address, so it takes a fixed narrow column and
 * lets the address absorb all remaining width. Sized in ch so the widest
 * option ("Bcc") always fits without truncating.
 */
.contact-entry__role {
  flex: 0 0 84px;
  min-inline-size: 84px;
}

/*
 * Row grouping relies on Vuetify's 16px field margin: it separates the top
 * line from the address below it, while the row margin (collapsing with the
 * address field's own margin) separates one recipient from the next. Within
 * stays tighter than between, so each control reads as belonging to its own
 * recipient.
 */
.contact-entry__remove {
  margin-block-start: 8px;
}
</style>